import { useEffect, useState } from 'react';
import { t } from 'i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBuilding, faRightToBracket, faTrash } from '@fortawesome/free-solid-svg-icons';
import { Button } from '../../components/Button/index';
import {
    XimiAccount,
    getXimiAccounts,
    removeXimiAccount
} from '../../utilities/ximi/storage/ximiAccounts';
import { useXimiSSOAuth } from '../../utilities/ximi/useXimiSSOAuth';
import { parseXimiProvisioningFromScanner } from '../../utilities/ximi/parseXimiProvisioning';
import { scanQr } from '../../utilities/scanner/qrScanner';
import { useBreakpoint } from '../../hooks/useBreakpoint';
import { Breakpoint } from '../../theme/breakpoints';
// TEMP désactivé : bouton Skip dev (compte de test QA) — réactiver au besoin
// import { PRODUCTION, XIMI_TEST_QA_LOGIN_HINT, XIMI_TEST_QA_TENANT_CODE } from '../../config';
import { XIMI_LOGIN_MANUAL_ENTRY_PATH } from '../../routePaths';
import QrCode from '../../assets/qrCode.svg?react';
import Pen from '../../assets/pen.svg?react';
import styles from './XimiSelectAccount.module.css';

/**
 * Intermediate page before the SSO: pick an existing Ximi account (pre-fills the SSO)
 * or scan a provisioning QR to add one. Web/PWA + native.
 */
const sameAccount = (a: XimiAccount, b: XimiAccount): boolean =>
    a.tenantCode === b.tenantCode && a.loginHint === b.loginHint;

/** Libellé principal, format Ximi : `NOM, Prénom [Structure]` — fallback tenantCode. */
const formatAccountLabel = (account: XimiAccount): string => {
    if (!account.displayName) return account.tenantCode;
    return account.structureName
        ? `${account.displayName} [${account.structureName}]`
        : account.displayName;
};

export function XimiSelectAccount(): JSX.Element {
    const [accounts, setAccounts] = useState<XimiAccount[]>([]);
    const [accountToRemove, setAccountToRemove] = useState<XimiAccount | null>(null);
    const [error, setError] = useState<string | null>(null);
    const isMobileLandscape = useBreakpoint() === Breakpoint.MOBILE_LANDSCAPE;
    const ximiSSO = useXimiSSOAuth();

    useEffect(() => {
        getXimiAccounts().then(setAccounts);
    }, []);

    const handleSelect = (account: XimiAccount) => {
        ximiSSO.login({ tenantCode: account.tenantCode, loginHint: account.loginHint });
    };

    const handleConfirmRemove = async (account: XimiAccount) => {
        await removeXimiAccount(account.tenantCode, account.loginHint);
        setAccountToRemove(null);
        setAccounts(await getXimiAccounts());
    };

    const handleScan = async () => {
        setError(null);
        try {
            const scanned = await scanQr();
            if (!scanned) return;
            const provisioning = parseXimiProvisioningFromScanner(scanned);
            if (!provisioning) {
                setError(t('Code QR Ximi invalide') ?? '');
                return;
            }
            const { tenantCode, loginHint } = provisioning;
            // Pas de stockage pré-login : persistance après auth SSO réussie (usePersistXimiAccount).
            await ximiSSO.login({ tenantCode, loginHint });
        } catch {
            setError(t('Le scan a échoué') ?? '');
        }
    };

    return (
        <div className={styles.container}>
            <div className={styles.panel}>
                <h1 className={styles.title}>{t('Connexion Ximi')}</h1>

                {accounts.length > 0 && (
                    <>
                        <p className={styles.listLabel}>{t('Comptes existants :')}</p>
                        <ul className={styles.list}>
                            {accounts.map((account) => (
                                <li
                                    key={`${account.tenantCode}|${account.loginHint}`}
                                    className={styles.accountRow}>
                                    {accountToRemove && sameAccount(accountToRemove, account) ? (
                                        <div className={styles.confirmRemove}>
                                            <span className={styles.confirmLabel}>
                                                {t('Supprimer ce compte ?')}
                                            </span>
                                            <button
                                                type="button"
                                                className={styles.confirmButton}
                                                onClick={() => handleConfirmRemove(account)}>
                                                {t('Supprimer')}
                                            </button>
                                            <button
                                                type="button"
                                                className={styles.cancelButton}
                                                onClick={() => setAccountToRemove(null)}>
                                                {t('Annuler')}
                                            </button>
                                        </div>
                                    ) : (
                                        <>
                                            <button
                                                type="button"
                                                className={styles.accountMain}
                                                onClick={() => handleSelect(account)}>
                                                <span className={styles.structurePicture}>
                                                    {account.structurePicture ? (
                                                        <img
                                                            src={account.structurePicture}
                                                            alt=""
                                                        />
                                                    ) : (
                                                        <FontAwesomeIcon icon={faBuilding} />
                                                    )}
                                                </span>
                                                <span className={styles.accountTexts}>
                                                    <span className={styles.accountName}>
                                                        {formatAccountLabel(account)}
                                                    </span>
                                                    <span className={styles.email}>
                                                        {account.email ?? account.loginHint}
                                                    </span>
                                                </span>
                                            </button>
                                            <button
                                                type="button"
                                                className={styles.iconButton}
                                                aria-label={t('Se connecter') ?? ''}
                                                onClick={() => handleSelect(account)}>
                                                <FontAwesomeIcon icon={faRightToBracket} />
                                            </button>
                                            <button
                                                type="button"
                                                className={styles.iconButton}
                                                aria-label={t('Supprimer') ?? ''}
                                                onClick={() => setAccountToRemove(account)}>
                                                <FontAwesomeIcon icon={faTrash} />
                                            </button>
                                        </>
                                    )}
                                </li>
                            ))}
                        </ul>
                        <hr className={styles.separator} />
                    </>
                )}

                <div className={styles.addCard}>
                    <p className={styles.addLabel}>{t('Ajouter un compte ximi :')}</p>
                    <Button
                        label={t('SCANNER') ?? ''}
                        Icon={QrCode}
                        size={isMobileLandscape ? 'SMALL' : 'MEDIUM'}
                        fullWidth
                        action={handleScan}
                    />
                    <Button
                        label={t('Saisie manuelle') ?? ''}
                        Icon={Pen}
                        theme="SECONDARY"
                        size={isMobileLandscape ? 'SMALL' : 'MEDIUM'}
                        fullWidth
                        action={XIMI_LOGIN_MANUAL_ENTRY_PATH}
                    />
                    {/* TEMP désactivé : bouton Skip dev (compte de test QA) — réactiver au besoin
                    {!PRODUCTION && (
                        <Button
                            label={t('Skip') ?? ''}
                            theme="SECONDARY"
                            size={isMobileLandscape ? 'SMALL' : 'MEDIUM'}
                            fullWidth
                            action={() =>
                                ximiSSO.login({
                                    tenantCode: XIMI_TEST_QA_TENANT_CODE,
                                    loginHint: XIMI_TEST_QA_LOGIN_HINT
                                })
                            }
                        />
                    )} */}
                    {error && <p className={styles.error}>{error}</p>}
                </div>

                <div className={styles.backButtonWrap}>
                    <Button
                        label={t('Retour à la connexion') ?? ''}
                        theme="GREY"
                        size={isMobileLandscape ? 'SMALL' : 'LEGACY'}
                        fullWidth
                        action="/login"
                    />
                </div>
            </div>
        </div>
    );
}
